/**
 * Coffee Quiz - Interactive Product Recommender
 * Uses bunamo metafields for intelligent coffee matching
 * 
 * Question Order: Caffeine → Brewing → Taste (3 questions)
 * 
 * Metafields used:
 * - brewingMethod: Zubereitungsmethode (Siebträger, Filter, Vollautomat, etc.)
 * - strength (0-10): Higher = stronger/bolder taste
 * - acidity (0-10): Higher = more fruity/bright
 * - tasteNotes: Text description of flavors
 */

const coffeeQuiz = (function () {
  // Debug mode - enable via URL param: ?debug-quiz=true
  const DEBUG = new URLSearchParams(window.location.search).has('debug-quiz');

  // STATS LOGGING - Always enabled for filter step analysis
  const STATS_LOGGING = true;

  // Store statistics for each quiz run
  let filterStats = {};

  function log(...args) {
    if (DEBUG) console.log('[Quiz Debug]', ...args);
  }

  function logTable(label, data) {
    if (DEBUG) {
      console.log('[Quiz Debug]', label);
      console.table(data);
    }
  }

  function logGroup(label) {
    if (DEBUG) console.group('[Quiz Debug] ' + label);
  }

  function logGroupEnd() {
    if (DEBUG) console.groupEnd();
  }

  function logStats(step, description, count, details = {}) {
    if (STATS_LOGGING) {
      filterStats[step] = { description, count, ...details };
      console.log(`📊 [FILTER STATS] Step ${step}: ${description} → ${count} coffees remaining`);
      if (details.excluded) {
        console.log(`   ↳ Excluded ${details.excluded} products`);
      }
      if (details.reason) {
        console.log(`   ↳ Reason: ${details.reason}`);
      }
    }
  }

  function logStatsSummary(answers) {
    if (STATS_LOGGING) {
      console.log('\n');
      console.log('═══════════════════════════════════════════════════════════════');
      console.log('📋 COFFEE QUIZ FILTER STATISTICS SUMMARY');
      console.log('═══════════════════════════════════════════════════════════════');
      console.log('User Answers:', JSON.stringify(answers, null, 2));
      console.log('───────────────────────────────────────────────────────────────');
      Object.keys(filterStats).sort().forEach(step => {
        const stat = filterStats[step];
        const padding = step.length < 3 ? '  ' : ' ';
        console.log(`Step ${step}:${padding}${stat.description.padEnd(35)} → ${String(stat.count).padStart(3)} coffees`);
      });
      console.log('═══════════════════════════════════════════════════════════════');
      console.log('\n');
    }
  }

  /**
   * Shuffle products within score buckets while preserving bucket priority.
   * Higher-score buckets stay above lower-score buckets.
   */
  function createSeededRandom(seed) {
    let state = (Number(seed) || 1) >>> 0;
    return function seededRandom() {
      // Mulberry32 PRNG
      state += 0x6D2B79F5;
      let t = state;
      t = Math.imul(t ^ (t >>> 15), t | 1);
      t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
      return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
    };
  }

  function shuffleWithinScoreBuckets(products, bucketSize, randomFn) {
    const size = Math.max(1, Number(bucketSize) || 1);
    const rand = typeof randomFn === 'function' ? randomFn : Math.random;
    const buckets = new Map();

    products.forEach((product) => {
      const score = Number(product.score) || 0;
      const bucketKey = Math.floor(score / size);
      if (!buckets.has(bucketKey)) buckets.set(bucketKey, []);
      buckets.get(bucketKey).push(product);
    });

    const orderedBucketKeys = Array.from(buckets.keys()).sort((a, b) => b - a);
    const shuffled = [];

    orderedBucketKeys.forEach((key) => {
      const bucket = buckets.get(key).slice();
      for (let i = bucket.length - 1; i > 0; i -= 1) {
        const j = Math.floor(rand() * (i + 1));
        const tmp = bucket[i];
        bucket[i] = bucket[j];
        bucket[j] = tmp;
      }
      shuffled.push(...bucket);
    });

    return shuffled;
  }

  /**
   * Pick a bucket size that actually creates shuffle candidates.
   * Tries wider buckets until enough products share buckets.
   */
  function selectAdaptiveBucketSize(products, candidates, minShufflableItems) {
    const list = Array.isArray(products) ? products : [];
    const bucketCandidates = Array.isArray(candidates) && candidates.length ? candidates : [2, 3, 4, 6, 8, 10, 12];
    const minItems = Math.max(2, Number(minShufflableItems) || 4);

    function countShufflableItems(size) {
      const map = new Map();
      list.forEach((product) => {
        const score = Number(product.score) || 0;
        const key = Math.floor(score / size);
        map.set(key, (map.get(key) || 0) + 1);
      });

      let shufflable = 0;
      map.forEach((count) => {
        if (count > 1) shufflable += count;
      });
      return shufflable;
    }

    let bestSize = bucketCandidates[0];
    let bestShufflable = countShufflableItems(bestSize);

    for (let i = 0; i < bucketCandidates.length; i += 1) {
      const size = bucketCandidates[i];
      const shufflable = countShufflableItems(size);
      if (shufflable > bestShufflable) {
        bestShufflable = shufflable;
        bestSize = size;
      }
      if (shufflable >= minItems) {
        return size;
      }
    }

    return bestSize;
  }

  function resetFilterStats() {
    filterStats = {};
  }

  // Quiz state storage
  const quizStates = {};

  // Question order mapping (3 questions)
  const questionOrder = ['caffeine', 'brewing', 'taste'];
  const TOTAL_QUESTIONS = 3;

  /**
   * Initialize quiz state for a section
   */
  function initQuizState(sectionId) {
    if (!quizStates[sectionId]) {
      quizStates[sectionId] = {
        currentStep: 0,
        answers: {
          caffeine: null,
          brewing: null,
          taste: null
        },
        shuffleRun: 0
      };
    }
    return quizStates[sectionId];
  }

  /**
   * Start the quiz
   */
  function startQuiz(sectionId) {
    const state = initQuizState(sectionId);
    const container = document.getElementById(`coffee-quiz-${sectionId}`);

    log('Starting quiz for section:', sectionId);
    log('Initial state:', JSON.stringify(state, null, 2));

    // Show sidebar
    const sidebar = document.getElementById(`quiz-sidebar-${sectionId}`);
    if (sidebar) {
      sidebar.classList.add('active');
      setupSidebarListeners(sectionId);
    }

    // Show reset button
    const resetWrapper = document.getElementById(`quiz-reset-wrapper-${sectionId}`);
    if (resetWrapper) {
      resetWrapper.classList.add('active');
    }

    // Hide intro, show first question
    hideAllStates(container);
    showQuestion(container, 1);
    state.currentStep = 1;
    updateSidebar(sectionId);
    updateProgressBar(sectionId);
  }

  /**
   * Handle answer selection
   */
  function selectAnswer(sectionId, questionType, answer) {
    const state = quizStates[sectionId];
    const container = document.getElementById(`coffee-quiz-${sectionId}`);

    log(`Answer selected: ${questionType} = "${answer}"`);

    // Store answer
    state.answers[questionType] = answer;
    log('Current answers:', JSON.stringify(state.answers, null, 2));

    // Update sidebar
    updateSidebar(sectionId);

    // Add animation to selected button
    const currentQuestion = container.querySelector('.quiz-question[data-question="' + state.currentStep + '"]');
    const buttons = currentQuestion.querySelectorAll('.quiz-answer');
    buttons.forEach(btn => btn.classList.add('answered'));

    // Wait for animation, then advance
    setTimeout(() => {
      if (state.currentStep < TOTAL_QUESTIONS) {
        // Move to next question
        state.currentStep++;
        hideAllStates(container);
        showQuestion(container, state.currentStep);
        updateSidebar(sectionId);
        updateProgressBar(sectionId);
      } else {
        // Show results
        calculateAndShowResults(sectionId);
      }
    }, 300);
  }

  /**
   * Calculate results using metafield-based scoring
   * 
   * Each product gets a score based on how well it matches:
   * - Caffeine preference (hard filter for decaf)
   * - Brewing method (Zubereitungsmethode metafield)
   * - Taste preference (acidity, strength, taste notes)
   */
  function calculateAndShowResults(sectionId) {
    const state = quizStates[sectionId];
    const answers = state.answers;

    // Get product data
    const data = window.coffeeQuizProducts[sectionId];
    if (!data || !data.products) {
      console.error('Product data not found for section:', sectionId);
      displayResults(
        sectionId,
        [],
        'Wir konnten keine Produktdaten laden. Bitte versuche es später erneut.'
      );
      return;
    }

    let products = [...data.products];
    let matchReasons = [];

    // Reset stats for this quiz run
    resetFilterStats();

    logGroup('CALCULATING RESULTS');

    // Log initial count
    logStats('0', 'Initial products loaded', products.length);

    // Debug: Show Liquid collection info and filtering breakdown
    if (data._debug) {
      log('📊 PRODUCT LOADING INFO:');
      log('  - Total products in Shopify "all" collection:', data._debug.collectionSize);
      log('  - Liquid loop limit:', data._debug.limit);
      log('  - Filter criteria:', data._debug.filterBy);
      log('  - Products passed to quiz:', products.length);

      // Show detailed Liquid filtering stats
      if (data._debug.stats) {
        console.log('\n');
        console.log('═══════════════════════════════════════════════════════════════');
        console.log('🔍 LIQUID PRE-FILTER BREAKDOWN (before JavaScript)');
        console.log('═══════════════════════════════════════════════════════════════');
        console.log(`Total products in collection:        ${data._debug.stats.total}`);
        console.log('───────────────────────────────────────────────────────────────');
        console.log(`❌ Unavailable (out of stock):       ${data._debug.stats.unavailable}`);
        console.log(`❌ No brewing method metafield:      ${data._debug.stats.noBrewingMethod}`);
        console.log(`❌ Invalid brewing method value:     ${data._debug.stats.invalidBrewingMethod}`);
        console.log('───────────────────────────────────────────────────────────────');
        console.log(`✅ Passed to quiz:                   ${data._debug.stats.passed}`);
        console.log('═══════════════════════════════════════════════════════════════');

        if (data._debug.excludedProducts) {
          const excluded = data._debug.excludedProducts;

          if (excluded.unavailable && excluded.unavailable.length > 0) {
            console.log('\n📦 Unavailable products (' + excluded.unavailable.length + '):');
            excluded.unavailable.forEach(p => console.log('   • ' + p));
          }

          if (excluded.noBrewingMethod && excluded.noBrewingMethod.length > 0) {
            console.log('\n⚠️ Products missing brewing method metafield (' + excluded.noBrewingMethod.length + '):');
            excluded.noBrewingMethod.forEach(p => console.log('   • ' + p));
          }

          if (excluded.invalidBrewingMethod && excluded.invalidBrewingMethod.length > 0) {
            console.log('\n⚠️ Products with invalid brewing method value (' + excluded.invalidBrewingMethod.length + '):');
            excluded.invalidBrewingMethod.forEach(p => console.log('   • ' + p));
          }
        }
        console.log('\n');
      }
    }

    log('User answers:', JSON.stringify(answers, null, 2));
    log('Total products in catalog:', products.length);

    if (products.length === 0) {
      logGroupEnd();
      displayResults(
        sectionId,
        [],
        'Aktuell sind keine passenden Kaffeeprodukte verfügbar.'
      );
      return;
    }

    // Debug: Log all products with their metafields
    if (DEBUG) {
      logTable('All Products', products.map(p => ({
        title: p.title,
        vendor: p.vendor,
        isDecaf: p.isDecaf,
        brewingMethod: toSafeString(p.brewingMethod).substring(0, 50),
        tasteNotes: toSafeString(p.tasteNotes).substring(0, 50)
      })));
    }

    // ===== STEP 1: CAFFEINE FILTER (Hard requirement) =====
    logGroup('STEP 1: Caffeine Filter');
    log('Caffeine preference:', answers.caffeine);
    const beforeCaffeineCount = products.length;

    if (answers.caffeine === 'decaf') {
      products = products.filter(p => p.isDecaf === true);
      matchReasons.push('Entkoffeiniert');
      log(`Filtered to decaf only: ${beforeCaffeineCount} → ${products.length} products`);
      logStats('1', `Caffeine: Decaf only`, products.length, { excluded: beforeCaffeineCount - products.length, reason: 'Filtered to decaf products only' });

      if (products.length === 0) {
        log('❌ No decaf products found!');
        logStatsSummary(answers);
        logGroupEnd();
        logGroupEnd();
        // Set state with empty results before displaying
        state.allResults = [];
        state.currentPage = 0;
        state.matchReason = 'Keine entkoffeinierten Kaffees gefunden';
        displayResults(sectionId);
        return;
      }
    } else {
      // Full caffeine selected - filter OUT decaf products
      const decafExcluded = products.filter(p => p.isDecaf === true);
      products = products.filter(p => p.isDecaf !== true);
      log(`Full caffeine selected - excluding ${decafExcluded.length} decaf products`);
      if (decafExcluded.length > 0) {
        logTable('Excluded decaf products', decafExcluded.map(p => ({ title: p.title })));
      }
      logStats('1', `Caffeine: Full power (exclude decaf)`, products.length, { excluded: decafExcluded.length, reason: 'Filtered out decaf products' });
    }
    logGroupEnd();

    // ===== STEP 2: BREWING METHOD FILTER (Hard requirement) =====
    logGroup('STEP 2: Brewing Method Filter');
    log('Brewing preference:', answers.brewing);
    const beforeBrewingCount = products.length;

    if (answers.brewing && answers.brewing !== 'flexible') {
      const excludedProducts = [];
      products = products.filter(p => {
        const brewingMethod = toSafeString(p.brewingMethod).toLowerCase();
        if (!brewingMethod || brewingMethod.length === 0) {
          excludedProducts.push({ title: p.title, reason: 'No brewing method set' });
          return false;
        }

        let matches = false;
        switch (answers.brewing) {
          case 'espresso':
            // Espresso → Siebträger, Mokakanne, Espresso-related
            matches = brewingMethod.includes('siebträger') ||
              brewingMethod.includes('siebtraeger') ||
              brewingMethod.includes('mokakanne') ||
              brewingMethod.includes('moka') ||
              brewingMethod.includes('espresso') ||
              brewingMethod.includes('portafilter') ||
              brewingMethod.includes('bialetti');
            break;
          case 'filter':
            // Filter → Filter, Aeropress, French Press, Chemex, V60, Pour Over
            matches = brewingMethod.includes('filter') ||
              brewingMethod.includes('aeropress') ||
              brewingMethod.includes('french press') ||
              brewingMethod.includes('frenchpress') ||
              brewingMethod.includes('chemex') ||
              brewingMethod.includes('v60') ||
              brewingMethod.includes('pour');
            break;
          case 'automatic':
            // Vollautomat → Vollautomat
            matches = brewingMethod.includes('vollautomat');
            break;
          default:
            matches = true;
        }

        if (!matches) {
          excludedProducts.push({ title: p.title, reason: `Method "${brewingMethod}" doesn't match "${answers.brewing}"` });
        }
        return matches;
      });

      // Mark products as "omniroast" if they support multiple brewing categories
      products = products.map(p => {
        const brewingMethod = toSafeString(p.brewingMethod).toLowerCase();

        // Check if product supports espresso-style brewing
        const hasEspresso = brewingMethod.includes('siebträger') ||
          brewingMethod.includes('siebtraeger') ||
          brewingMethod.includes('mokakanne') ||
          brewingMethod.includes('moka') ||
          brewingMethod.includes('espresso');

        // Check if product supports filter-style brewing
        const hasFilter = brewingMethod.includes('filter') ||
          brewingMethod.includes('aeropress') ||
          brewingMethod.includes('french press') ||
          brewingMethod.includes('frenchpress') ||
          brewingMethod.includes('chemex') ||
          brewingMethod.includes('v60') ||
          brewingMethod.includes('pour');

        // Product is omniroast if it supports BOTH espresso and filter
        const isOmniroast = hasEspresso && hasFilter;

        return { ...p, isOmniroast, hasEspresso, hasFilter };
      });

      matchReasons.push(getBrewingLabel(answers.brewing));

      log(`Filtered by brewing: ${beforeBrewingCount} → ${products.length} products`);
      const omniroastCount = products.filter(p => p.isOmniroast).length;
      log(`Omniroast products (will be penalized): ${omniroastCount}`);
      if (excludedProducts.length > 0) {
        logTable('Excluded products (brewing)', excludedProducts);
      }
      logStats('2', `Brewing: ${getBrewingLabel(answers.brewing)}`, products.length, { excluded: beforeBrewingCount - products.length, reason: `Filtered for ${answers.brewing} brewing method` });

      if (products.length === 0) {
        log('❌ No products match brewing method!');
        logStatsSummary(answers);
        logGroupEnd();
        logGroupEnd();
        // Set state with empty results before displaying
        state.allResults = [];
        state.currentPage = 0;
        state.matchReason = `Keine Kaffees für ${getBrewingLabel(answers.brewing)} gefunden`;
        displayResults(sectionId);
        return;
      }
    } else {
      log('Flexible brewing selected - no filter applied');
      logStats('2', `Brewing: Flexible (no filter)`, products.length, { excluded: 0, reason: 'No brewing restriction applied' });
    }
    logGroupEnd();

    // ===== STEP 3: TASTE SCORING (Soft scoring instead of hard filter) =====
    logGroup('STEP 3: Taste Scoring');
    log('Taste preference:', answers.taste);
    const beforeTasteCount = products.length;

    // Calculate taste scores for all products
    if (answers.taste) {
      const scoredProducts = [];
      const productsWithoutNotes = [];

      products.forEach(p => {
        const tasteNotes = toSafeString(p.tasteNotes).toLowerCase();
        if (!tasteNotes || tasteNotes.length === 0) {
          productsWithoutNotes.push({ title: p.title, reason: 'No taste notes set' });
          // Give products without taste notes a score of 0 (neutral)
          scoredProducts.push({ ...p, tasteScore: 0, matchedTastes: [], allScores: { fruity: 0, nutty: 0, spicy: 0, tealike: 0 } });
          return;
        }

        const { preferenceScore, allScores, matchedTastes } = getPreferenceScore(tasteNotes, answers.taste);
        scoredProducts.push({
          ...p,
          tasteScore: preferenceScore,
          matchedTastes,
          allScores
        });
      });

      // Sort by taste score (highest first)
      scoredProducts.sort((a, b) => b.tasteScore - a.tasteScore);

      // Log detailed scoring analysis
      console.log('\n');
      console.log('🎯 ═══════════════════════════════════════════════════════════════');
      console.log(`🎯 TASTE SCORING ANALYSIS - Preference: "${answers.taste}"`);
      console.log('🎯 ═══════════════════════════════════════════════════════════════');

      // Summary statistics
      const scores = scoredProducts.map(p => p.tasteScore);
      const maxScore = Math.max(...scores);
      const minScore = Math.min(...scores);
      const avgScore = scores.reduce((a, b) => a + b, 0) / scores.length;
      const positiveCount = scores.filter(s => s > 0).length;
      const neutralCount = scores.filter(s => s === 0).length;
      const negativeCount = scores.filter(s => s < 0).length;

      console.log(`📊 Score Range: ${minScore} to ${maxScore} (avg: ${avgScore.toFixed(1)})`);
      console.log(`📊 Positive scores (>0): ${positiveCount} products`);
      console.log(`📊 Neutral scores (=0): ${neutralCount} products`);
      console.log(`📊 Negative scores (<0): ${negativeCount} products`);
      console.log('───────────────────────────────────────────────────────────────');

      // Top 10 products table
      console.log('🏆 TOP 10 SCORING PRODUCTS:');
      logTable('Taste Scores', scoredProducts.slice(0, 10).map(p => ({
        title: p.title,
        tasteScore: p.tasteScore,
        fruity: p.allScores.fruity,
        nutty: p.allScores.nutty,
        spicy: p.allScores.spicy,
        tealike: p.allScores.tealike,
        matchedCount: p.matchedTastes.length,
        tasteNotes: toSafeString(p.tasteNotes).substring(0, 40)
      })));

      // Show matched taste breakdown for top 3
      console.log('───────────────────────────────────────────────────────────────');
      console.log('🔍 TASTE BREAKDOWN FOR TOP 3:');
      scoredProducts.slice(0, 3).forEach((p, i) => {
        console.log(`\n  ${i + 1}. ${p.title} (Score: ${p.tasteScore})`);
        if (p.matchedTastes.length > 0) {
          p.matchedTastes.slice(0, 5).forEach(t => {
            const scoreKey = answers.taste === 'fruity' ? 'fruity' : answers.taste === 'nutty' ? 'nutty' : answers.taste === 'spicy' ? 'spicy' : 'tealike';
            const contribution = t[scoreKey] || 0;
            const sign = contribution >= 0 ? '+' : '';
            console.log(`     • "${t.taste}": ${sign}${contribution} (F:${t.fruity}/N:${t.nutty}/S:${t.spicy}/T:${t.tealike})`);
          });
          if (p.matchedTastes.length > 5) {
            console.log(`     ... and ${p.matchedTastes.length - 5} more tastes`);
          }
        } else {
          console.log('     (no taste notes matched)');
        }
      });
      console.log('\n🎯 ═══════════════════════════════════════════════════════════════\n');

      if (productsWithoutNotes.length > 0) {
        log(`Note: ${productsWithoutNotes.length} products have no taste notes (scored as 0)`);
      }

      // Replace products with scored versions
      products = scoredProducts;
      matchReasons.push(getTasteLabel(answers.taste));

      log(`Scored ${products.length} products for taste preference "${answers.taste}"`);
      logStats('3', `Taste: ${getTasteLabel(answers.taste)} (scoring)`, products.length, {
        excluded: 0,
        reason: `Scored based on ${answers.taste} preference (no products excluded)`
      });
    } else {
      log('No specific taste preference - balanced scoring');
      products = products.map(p => ({
        ...p,
        tasteScore: 0,
        matchedTastes: [],
        allScores: { fruity: 0, nutty: 0, spicy: 0, tealike: 0 }
      }));
      logStats('3', `Taste: None specified (no filter)`, products.length, { excluded: 0, reason: 'No taste restriction applied' });
    }
    logGroupEnd();

    // ===== STEP 4: FINAL SELECTION (Combine taste score + randomness) =====
    logGroup('STEP 4: Final Selection');
    log(`Products after all filters: ${products.length}`);

    const finalScoredProducts = products.map(product => {
      // Get user's preference and all category scores
      const preference = state.answers.taste;
      const allScores = product.allScores || { fruity: 0, nutty: 0, spicy: 0, tealike: 0 };
      const preferenceScore = product.tasteScore;

      // ===== CROSS-CATEGORY PENALTY =====
      // Define opposing categories that should penalize each other
      // Fruity <-> Nutty are opposites (light/bright vs dark/rich)
      // Spicy <-> Tealike are somewhat opposing (bold/spicy vs delicate/floral)
      const opposingCategories = {
        fruity: 'nutty',
        nutty: 'fruity',
        spicy: 'tealike',
        tealike: 'spicy'
      };

      // Penalty multipliers - reduced for higher match scores overall
      // (lower values = less penalty = higher scores)
      const penaltyMultipliers = {
        fruity: 0.4,
        nutty: 0.4,
        spicy: 0.4,
        tealike: 0.2  // Reduced penalty for tealike - more tolerant of mixed flavors
      };

      // Calculate cross-penalty: if opposing category has high positive score, penalize
      let crossPenalty = 0;
      const opposingCategory = opposingCategories[preference];
      const opposingScore = allScores[opposingCategory] || 0;
      const penaltyMultiplier = penaltyMultipliers[preference] || 0.8;

      if (opposingScore > 0) {
        // Penalty is proportional to opposing category's strength
        crossPenalty = opposingScore * penaltyMultiplier;
      }

      // Also penalize for any other category that scores high (dominance penalty)
      const otherCategories = ['fruity', 'nutty', 'spicy', 'tealike'].filter(c => c !== preference);
      const maxOtherScore = Math.max(...otherCategories.map(c => allScores[c]));

      let dominancePenalty = 0;
      if (preferenceScore > 0 && maxOtherScore > preferenceScore) {
        // Penalize when another category dominates (reduced for higher scores)
        const dominanceMultiplier = preference === 'tealike' ? 0.2 : 0.5;
        dominancePenalty = (maxOtherScore - preferenceScore) * dominanceMultiplier;
      }

      // Apply both penalties (but don't double-count the opposing category)
      // Cross-penalty already covers opposing, so we use the higher of the two
      const totalPenalty = Math.max(crossPenalty, dominancePenalty);
      let adjustedTasteScore = preferenceScore - totalPenalty;

      // Clamp to reasonable range (-30 to +30) for normalization
      adjustedTasteScore = Math.max(-30, Math.min(30, adjustedTasteScore));

      // ===== METAFIELD BONUS SCORING =====
      // Get metafield values (1-10 scale, or null if not set)
      const roastLevel = product.roastLevel; // 1=light, 10=dark
      const strength = product.strength;     // 1=mild, 10=strong
      const acidity = product.acidity;       // 1=low, 10=high

      // Calculate metafield bonus based on preference (only if metafield exists)
      // Bonus ranges from -10 to +10 based on how well metafield matches preference
      let metafieldBonus = 0;
      let metafieldFactors = [];

      if (preference === 'nutty') {
        // Nutty: prefer dark roast (high), high strength, low acidity
        if (roastLevel !== null && roastLevel !== undefined) {
          // roastLevel 1→-5, 5→0, 10→+5
          const roastBonus = (roastLevel - 5) * 1;
          metafieldBonus += roastBonus;
          metafieldFactors.push({ name: 'roast', value: roastLevel, bonus: roastBonus });
        }
        if (strength !== null && strength !== undefined) {
          // strength 1→-5, 5→0, 10→+5
          const strengthBonus = (strength - 5) * 1;
          metafieldBonus += strengthBonus;
          metafieldFactors.push({ name: 'strength', value: strength, bonus: strengthBonus });
        }
        if (acidity !== null && acidity !== undefined) {
          // LOW acidity is good: acidity 1→+5, 5→0, 10→-5
          const acidityBonus = (5 - acidity) * 1;
          metafieldBonus += acidityBonus;
          metafieldFactors.push({ name: 'acidity', value: acidity, bonus: acidityBonus });
        }
      } else if (preference === 'fruity') {
        // Fruity: prefer high acidity
        if (acidity !== null && acidity !== undefined) {
          // HIGH acidity is good: acidity 1→-5, 5→0, 10→+5
          const acidityBonus = (acidity - 5) * 1;
          metafieldBonus += acidityBonus;
          metafieldFactors.push({ name: 'acidity', value: acidity, bonus: acidityBonus });
        }
      } else if (preference === 'tealike') {
        // Tealike/floral: prefer low strength
        if (strength !== null && strength !== undefined) {
          // LOW strength is good: strength 1→+5, 5→0, 10→-5
          const strengthBonus = (5 - strength) * 1;
          metafieldBonus += strengthBonus;
          metafieldFactors.push({ name: 'strength', value: strength, bonus: strengthBonus });
        }
      }
      // Spicy has no specific metafield preferences

      // ===== OMNIROAST PENALTY =====
      // If user selected a specific brewing method (espresso or filter) and product is omniroast,
      // apply a small penalty since specialized roasts are preferred
      let omniroastPenalty = 0;
      const brewingPreference = state.answers.brewing;
      if (brewingPreference && brewingPreference !== 'flexible' && brewingPreference !== 'automatic') {
        if (product.isOmniroast) {
          // Omniroast penalty: -8 points (translates to roughly -5% match)
          omniroastPenalty = 8;
        }
      }

      // ===== 100% ROBUSTA PENALTY =====
      // Always penalize coffees that are pure robusta, independent of quiz answers.
      let robustaPenalty = 0;
      const robustaShare = Number(product.robustaShare);
      if (!Number.isNaN(robustaShare) && robustaShare >= 100) {
        // Strong quality penalty: always down-rank 100% robusta coffees.
        robustaPenalty = 10;
      }

      // ===== COMBINED SCORING =====
      // Taste score ranges -30 to +30 after adjustment
      // Metafield bonus ranges -15 to +15
      // Omniroast penalty: 0 or -8
      // Robusta penalty: 0 or -10 (100% robusta only)
      // 
      // Match percentage formula:
      // - Taste score -30 → 40% base (very bad match)
      // - Taste score 0 → 70% base (neutral)
      // - Taste score +30 → 100% base (perfect match)
      // - Metafield bonus adds/subtracts up to ±10%
      // - Omniroast penalty subtracts up to 5%
      // - 100% Robusta penalty subtracts up to 6%
      //
      // Formula: 70 + (adjustedTasteScore * 1) + (metafieldBonus * 0.67) - (omniroastPenalty * 0.6) - (robustaPenalty * 0.6)
      const tasteMatchBase = 70 + adjustedTasteScore; // -30→40%, 0→70%, +30→100%
      const metafieldAdjustment = metafieldBonus * 0.67; // -15→-10%, +15→+10%
      const omniroastAdjustment = omniroastPenalty * 0.6; // 0 or -5%
      const robustaAdjustment = robustaPenalty * 0.6; // 0 or -6%
      const matchPercent = Math.round(Math.min(100, Math.max(40, tasteMatchBase + metafieldAdjustment - omniroastAdjustment - robustaAdjustment)));

      // Final score for sorting (same components, but we want a larger range for sorting precision)
      const finalScore = (adjustedTasteScore + 30) * 2 + metafieldBonus - omniroastPenalty - robustaPenalty; // 0-120 range + bonus - penalty

      // Debug logging for specific products or high scores
      if (product.title.toLowerCase().includes('zacatin') || product.title.toLowerCase().includes('hazelnut') || matchPercent >= 95) {
        console.log('\n🔍 ═══════════════════════════════════════════════════════════════');
        console.log(`🔍 SCORE ANALYSIS: ${product.title}`);
        console.log('🔍 ═══════════════════════════════════════════════════════════════');
        console.log(`📝 Taste Notes (raw): "${toSafeString(product.tasteNotes)}"`);
        console.log(`🎯 User Preference: "${preference}" (opposing: ${opposingCategory})`);
        console.log(`📊 Raw Preference Score: ${preferenceScore}`);
        console.log(`📊 All Category Scores: F:${allScores.fruity} / N:${allScores.nutty} / S:${allScores.spicy} / T:${allScores.tealike}`);
        console.log(`📊 Cross-Penalty: opposing ${opposingCategory}=${opposingScore} → -${crossPenalty.toFixed(1)}`);
        console.log(`📊 Dominance Penalty: -${dominancePenalty.toFixed(1)} | Total Penalty: -${totalPenalty.toFixed(1)}`);
        console.log(`📊 Adjusted Score: ${preferenceScore} - ${totalPenalty.toFixed(1)} = ${adjustedTasteScore.toFixed(1)}`);
        console.log(`📊 Metafields: roast=${roastLevel ?? 'n/a'} | strength=${strength ?? 'n/a'} | acidity=${acidity ?? 'n/a'}`);
        if (metafieldFactors.length > 0) {
          console.log(`📊 Metafield Bonuses: ${metafieldFactors.map(f => `${f.name}(${f.value})=${f.bonus > 0 ? '+' : ''}${f.bonus}`).join(', ')} → Total: ${metafieldBonus > 0 ? '+' : ''}${metafieldBonus}`);
        } else {
          console.log(`📊 Metafield Bonuses: none (no applicable metafields)`);
        }
        console.log(`📊 Omniroast: ${product.isOmniroast ? 'YES → penalty -' + omniroastPenalty : 'no'}`);
        console.log(`📊 Robusta: ${Number.isNaN(robustaShare) ? 'n/a' : robustaShare + '%'}${robustaPenalty > 0 ? ' → penalty -' + robustaPenalty : ''}`);
        console.log(`📊 Match Calculation: 70 + (${adjustedTasteScore.toFixed(1)}) + (${metafieldBonus} * 0.67) - (${omniroastPenalty} * 0.6) - (${robustaPenalty} * 0.6) = ${(tasteMatchBase + metafieldAdjustment - omniroastAdjustment - robustaAdjustment).toFixed(1)} → ${matchPercent}%`);
        console.log(`📊 Sort Score: ${finalScore.toFixed(1)}`);
        if (product.matchedTastes && product.matchedTastes.length > 0) {
          console.log('🍃 Matched Tastes:');
          product.matchedTastes.forEach(t => {
            const contribution = t[preference] || 0;
            console.log(`   • "${t.taste}": ${preference}=${contribution} (F:${t.fruity}/N:${t.nutty}/S:${t.spicy}/T:${t.tealike})`);
          });
        }
        console.log('🔍 ═══════════════════════════════════════════════════════════════\n');
      }

      return {
        ...product,
        score: finalScore,
        matchPercent,
        metafieldBonus,
        omniroastPenalty,
        robustaPenalty,
        metafieldFactors,
        matchDetails: product.matchedTastes || []
      };
    });

    // Sort into score buckets, then shuffle within each bucket.
    // This adds variety without changing any product score/match percentage.
    const SCORE_BUCKET_SIZE = selectAdaptiveBucketSize(finalScoredProducts, [2, 3, 4, 6, 8, 10, 12], 6);
    state.shuffleRun = (state.shuffleRun || 0) + 1;
    const shuffleSeed = ((Date.now() & 0xFFFFFFFF) ^ ((state.shuffleRun * 2654435761) >>> 0)) >>> 0;
    const runRandom = createSeededRandom(shuffleSeed);
    const sortedByScore = finalScoredProducts.sort((a, b) => b.score - a.score);
    const bucketShuffledProducts = shuffleWithinScoreBuckets(sortedByScore, SCORE_BUCKET_SIZE, runRandom);

    // Store all products for pagination
    state.allResults = bucketShuffledProducts;
    state.currentPage = 0;
    // Show 1 product on narrow screens, 3 on wider screens
    state.productsPerPage = window.innerWidth < 990 ? 1 : 3;
    state.matchReason = matchReasons.join(' • ');

    // Log top products
    const topProducts = bucketShuffledProducts.slice(0, 4);
    log('✅ Final recommendations (sorted by combined score):');
    logTable('Selected Products', topProducts.map(p => ({
      title: p.title,
      vendor: p.vendor,
      tasteScore: p.tasteScore,
      metafieldBonus: p.metafieldBonus || 0,
      finalScore: Math.round(p.score),
      matchPercent: p.matchPercent + '%'
    })));

    logStats('4', `Final: ${bucketShuffledProducts.length} total matches`, bucketShuffledProducts.length, { reason: 'Selected by taste score with variety' });
    logStatsSummary(answers);

    logGroupEnd();
    logGroupEnd();

    // Display results with pagination
    displayResults(sectionId);
  }

  /**
   * Helper: Safely convert metafield value to string
   * Metafields can be strings, objects, arrays, or null
   */
  function toSafeString(value) {
    if (value === null || value === undefined) return '';
    if (typeof value === 'string') return value;
    // Handle array - could be strings, objects with 'name', or objects with 'value'
    if (Array.isArray(value)) {
      return value.map(item => {
        if (typeof item === 'string') return item;
        if (typeof item === 'object' && item !== null) {
          // Try common property names for the display value
          return item.name || item.value || item.label || item.title || JSON.stringify(item);
        }
        return String(item);
      }).join(', ');
    }
    if (typeof value === 'object') {
      // Try common property names
      return value.name || value.value || value.label || value.title || JSON.stringify(value);
    }
    return String(value);
  }

  /**
   * TASTE SCORE DATABASE - Based on tastedatabase_v2.csv
   * Uses shared data from bunamo-taste-data.js when available
   * Each taste has scores for fruity, nutty, spicy, and tealike preferences
   * Scores range from -10 to 10
   */
  const TASTE_SCORES = (window.BunamoTasteData && window.BunamoTasteData.TASTE_SCORES) || {
    // Nussig/Kakao > Kakao
    'weiße schokolade': { fruity: -3, nutty: 8, spicy: 0, tealike: 0 },
    'schokoladen torte': { fruity: -3, nutty: 9, spicy: 0, tealike: 0 },
    'praline': { fruity: -2, nutty: 9, spicy: 0, tealike: 0 },
    'mocha': { fruity: -2, nutty: 8, spicy: 0, tealike: 0 },
    'schokolade': { fruity: -3, nutty: 10, spicy: 0, tealike: 0 },
    'zartbitterschokolade': { fruity: -4, nutty: 9, spicy: 0, tealike: 0 },
    'milchschokolade': { fruity: -2, nutty: 10, spicy: 0, tealike: 0 },
    'bitterschokolade': { fruity: -4, nutty: 8, spicy: 0, tealike: 0 },
    'brownie': { fruity: -3, nutty: 9, spicy: 0, tealike: 0 },
    'ovomaltine': { fruity: -2, nutty: 8, spicy: 0, tealike: 0 },
    'tiramisu': { fruity: -2, nutty: 8, spicy: 0, tealike: 0 },
    'nougat': { fruity: -3, nutty: 10, spicy: 0, tealike: 0 },
    'kakao': { fruity: -3, nutty: 10, spicy: 0, tealike: 0 },
    'noisette schokolade': { fruity: -2, nutty: 9, spicy: 0, tealike: 0 },
    'schokokeks': { fruity: -2, nutty: 8, spicy: 0, tealike: 0 },
    'kakaonibs': { fruity: -3, nutty: 9, spicy: 0, tealike: 0 },

    // Fruchtig > Trockenfrüchte
    'trockenfrüchte': { fruity: 8, nutty: 2, spicy: 0, tealike: 0 },
    'rosine': { fruity: 7, nutty: 3, spicy: 0, tealike: 0 },
    'getrocknete mango': { fruity: 9, nutty: 0, spicy: 0, tealike: 0 },
    'dattel': { fruity: 7, nutty: 3, spicy: 0, tealike: 0 },
    'rumrosine': { fruity: 6, nutty: 3, spicy: 0, tealike: 0 },
    'getrockneter apfel': { fruity: 8, nutty: 1, spicy: 0, tealike: 0 },
    'pflaume': { fruity: 9, nutty: 0, spicy: 0, tealike: 0 },
    'getrocknete tomate': { fruity: 4, nutty: 0, spicy: 2, tealike: 0 },

    // Blumig - High tealike scores
    'orangenblüte': { fruity: 7, nutty: 0, spicy: 0, tealike: 8 },
    'blumig': { fruity: 6, nutty: 0, spicy: 0, tealike: 10 },
    'kamille': { fruity: 2, nutty: 0, spicy: 0, tealike: 10 },
    'tee': { fruity: 2, nutty: 0, spicy: 0, tealike: 10 },
    'lilie': { fruity: 3, nutty: 0, spicy: 0, tealike: 9 },
    'kaffeeblüte': { fruity: 4, nutty: 0, spicy: 0, tealike: 8 },
    'apfelblüte': { fruity: 6, nutty: 0, spicy: 0, tealike: 8 },
    'kirschblüte': { fruity: 6, nutty: 0, spicy: 0, tealike: 9 },
    'jasmin': { fruity: 4, nutty: 0, spicy: 0, tealike: 10 },
    'veilchen': { fruity: 3, nutty: 0, spicy: 0, tealike: 9 },
    'lavendel': { fruity: 2, nutty: 0, spicy: 0, tealike: 9 },
    'holunderblüte': { fruity: 6, nutty: 0, spicy: 0, tealike: 8 },
    'rose': { fruity: 4, nutty: 0, spicy: 0, tealike: 10 },

    // Fruchtig
    'fruchtig': { fruity: 10, nutty: 0, spicy: 0, tealike: 0 },

    // Grün/Pflanzlich - Moderate tealike
    'grün/pflanzlich': { fruity: 2, nutty: -1, spicy: 0, tealike: 4 },
    'pflanzlich': { fruity: 1, nutty: -1, spicy: 0, tealike: 3 },
    'kräuterartig': { fruity: 0, nutty: -1, spicy: 2, tealike: 6 },
    'erbsenschote': { fruity: 0, nutty: -2, spicy: 0, tealike: 2 },
    'unreif': { fruity: 1, nutty: -2, spicy: 0, tealike: 1 },
    'frisch': { fruity: 3, nutty: 0, spicy: 0, tealike: 3 },
    'eukalyptus': { fruity: 1, nutty: -1, spicy: 2, tealike: 5 },
    'dunkelgrün': { fruity: 0, nutty: -1, spicy: 0, tealike: 3 },
    'heuartig': { fruity: 0, nutty: -2, spicy: 0, tealike: 3 },
    'fichtennadel': { fruity: 0, nutty: -2, spicy: 2, tealike: 4 },

    // Kräuter - Tealike with some spicy
    'kräuter': { fruity: 0, nutty: -1, spicy: 3, tealike: 7 },
    'salbei': { fruity: 0, nutty: -1, spicy: 3, tealike: 6 },
    'hopfen': { fruity: 0, nutty: -1, spicy: 2, tealike: 5 },
    'thymian': { fruity: 0, nutty: -1, spicy: 4, tealike: 5 },
    'citronenmelisse': { fruity: 3, nutty: 0, spicy: 0, tealike: 7 },
    'kräuter der provence': { fruity: 0, nutty: -1, spicy: 4, tealike: 6 },
    'minze': { fruity: 2, nutty: 0, spicy: 2, tealike: 7 },
    'rosmarin': { fruity: 0, nutty: -1, spicy: 4, tealike: 5 },

    // Nussig/Kakao
    'krokant': { fruity: -2, nutty: 8, spicy: 0, tealike: 0 },
    'pistazie': { fruity: -1, nutty: 9, spicy: 0, tealike: 0 },
    'geröstere mandel': { fruity: -2, nutty: 10, spicy: 0, tealike: 0 },
    'pekanuss': { fruity: -1, nutty: 10, spicy: 0, tealike: 0 },
    'salzige mandel': { fruity: -2, nutty: 9, spicy: 0, tealike: 0 },
    'pinienkerne': { fruity: 0, nutty: 8, spicy: 0, tealike: 0 },
    'geröstete nüsse': { fruity: -2, nutty: 10, spicy: 0, tealike: 0 },
    'marzipan': { fruity: 0, nutty: 9, spicy: 0, tealike: 0 },
    'haselnuss': { fruity: -2, nutty: 10, spicy: 0, tealike: 0 },
    'paranuss': { fruity: -1, nutty: 9, spicy: 0, tealike: 0 },
    'erdnussbutter': { fruity: -2, nutty: 10, spicy: 0, tealike: 0 },
    'walnuss': { fruity: -1, nutty: 9, spicy: 0, tealike: 0 },
    'mandel': { fruity: 0, nutty: 10, spicy: 0, tealike: 0 },
    'erdnuss': { fruity: -1, nutty: 10, spicy: 0, tealike: 0 },
    'makadamianuss': { fruity: 0, nutty: 9, spicy: 0, tealike: 0 },
    'cashew': { fruity: 0, nutty: 9, spicy: 0, tealike: 0 },
    'nussig/kakao': { fruity: -2, nutty: 10, spicy: 0, tealike: 0 },
    'marone': { fruity: 0, nutty: 8, spicy: 0, tealike: 0 },
    'cola': { fruity: 2, nutty: 3, spicy: 3, tealike: 0 },

    // Olivenöl
    'olivenöl': { fruity: 0, nutty: 2, spicy: 2, tealike: 0 },

    // Sonstige
    'kaugummi': { fruity: 2, nutty: 0, spicy: 0, tealike: 0 },
    'sonstige': { fruity: 0, nutty: 0, spicy: 0, tealike: 0 },

    // Andere Früchte
    'hagebutte': { fruity: 8, nutty: 0, spicy: 0, tealike: 4 },
    'andere früchte': { fruity: 8, nutty: 0, spicy: 0, tealike: 0 },
    'aprikose': { fruity: 10, nutty: 0, spicy: 0, tealike: 0 },
    'papaya': { fruity: 10, nutty: 0, spicy: 0, tealike: 0 },
    'kokusnuss': { fruity: 4, nutty: 5, spicy: 0, tealike: 0 },
    'sternfrucht': { fruity: 9, nutty: 0, spicy: 0, tealike: 0 },
    'lychee': { fruity: 10, nutty: 0, spicy: 0, tealike: 3 },
    'guave': { fruity: 10, nutty: 0, spicy: 0, tealike: 0 },
    'reife früchte': { fruity: 9, nutty: 0, spicy: 0, tealike: 0 },
    'granatapfel': { fruity: 10, nutty: 0, spicy: 0, tealike: 0 },
    'kirsche': { fruity: 10, nutty: 0, spicy: 0, tealike: 0 },
    'birne': { fruity: 9, nutty: 0, spicy: 0, tealike: 0 },
    'mirabelle': { fruity: 9, nutty: 0, spicy: 0, tealike: 0 },
    'melone': { fruity: 8, nutty: 0, spicy: 0, tealike: 0 },
    'grüner apfel': { fruity: 9, nutty: 0, spicy: 0, tealike: 0 },
    'wassermelone': { fruity: 8, nutty: 0, spicy: 0, tealike: 0 },
    'passionsfrucht': { fruity: 10, nutty: 0, spicy: 0, tealike: 0 },
    'apfel': { fruity: 9, nutty: 0, spicy: 0, tealike: 0 },
    'rote trauben': { fruity: 9, nutty: 0, spicy: 0, tealike: 0 },
    'feige': { fruity: 8, nutty: 2, spicy: 0, tealike: 0 },
    'hibiskus': { fruity: 7, nutty: 0, spicy: 0, tealike: 7 },
    'steinfrucht': { fruity: 9, nutty: 0, spicy: 0, tealike: 0 },
    'pfirsich': { fruity: 10, nutty: 0, spicy: 0, tealike: 0 },
    'nektarine': { fruity: 10, nutty: 0, spicy: 0, tealike: 0 },
    'tropische früchte': { fruity: 10, nutty: 0, spicy: 0, tealike: 0 },
    'rote früchte': { fruity: 10, nutty: 0, spicy: 0, tealike: 0 },
    'banane': { fruity: 7, nutty: 2, spicy: 0, tealike: 0 },
    'roter apfel': { fruity: 9, nutty: 0, spicy: 0, tealike: 0 },
    'ananas': { fruity: 10, nutty: 0, spicy: 0, tealike: 0 },
    'süße früchte': { fruity: 9, nutty: 1, spicy: 0, tealike: 0 },
    'honigmelone': { fruity: 8, nutty: 1, spicy: 0, tealike: 0 },
    'mango': { fruity: 10, nutty: 0, spicy: 0, tealike: 0 },
    'traube': { fruity: 8, nutty: 0, spicy: 0, tealike: 0 },
    'gelbe früchte': { fruity: 10, nutty: 0, spicy: 0, tealike: 0 },

    // Generell Süsslich
    'generell süsslich': { fruity: 2, nutty: 4, spicy: 0, tealike: 0 },

    // Papierartig/Modrig (negative scores)
    'phenolartig': { fruity: -3, nutty: -2, spicy: 0, tealike: -2 },
    'modrig/erdig': { fruity: -3, nutty: -2, spicy: 0, tealike: -2 },
    'schal': { fruity: -2, nutty: -1, spicy: 0, tealike: -1 },
    'pappartig': { fruity: -2, nutty: -1, spicy: 0, tealike: -1 },
    'erdig': { fruity: -2, nutty: 0, spicy: 2, tealike: -1 },
    'muffig/feucht': { fruity: -3, nutty: -2, spicy: 0, tealike: -2 },
    'fleischig/brühe': { fruity: -3, nutty: -1, spicy: 3, tealike: -3 },
    'modrig/staubig': { fruity: -3, nutty: -2, spicy: 0, tealike: -2 },
    'papierartig/modrig': { fruity: -2, nutty: -1, spicy: 0, tealike: -1 },
    'papierartig': { fruity: -2, nutty: -1, spicy: 0, tealike: -1 },
    'holzig': { fruity: -1, nutty: 1, spicy: 3, tealike: 2 },
    'tierartig': { fruity: -4, nutty: -2, spicy: 2, tealike: -3 },

    // Pfeffer - High spicy
    'pfeffer': { fruity: -2, nutty: 0, spicy: 10, tealike: 0 },

    // Pfeifentabak/Tabak
    'pfeifentabak': { fruity: -3, nutty: 2, spicy: 4, tealike: 0 },
    'tabak': { fruity: -4, nutty: 2, spicy: 4, tealike: 0 },

    // Beißend - Spicy
    'beißend': { fruity: -2, nutty: 0, spicy: 7, tealike: 0 },

    // Roh
    'roh': { fruity: 0, nutty: -2, spicy: 0, tealike: 0 },

    // Geröstet
    'geröstet': { fruity: -4, nutty: 3, spicy: 2, tealike: 0 },

    // Sauer
    'essigsäure': { fruity: 3, nutty: -2, spicy: 2, tealike: 0 },
    'zitronensäure': { fruity: 6, nutty: -1, spicy: 0, tealike: 0 },
    'sauer': { fruity: 4, nutty: -1, spicy: 0, tealike: 0 },
    'sahne': { fruity: 0, nutty: 4, spicy: 0, tealike: 0 },
    'joghurt': { fruity: 3, nutty: 1, spicy: 0, tealike: 0 },
    'saure aromen': { fruity: 4, nutty: -1, spicy: 0, tealike: 0 },
    'apfelsäure': { fruity: 5, nutty: -1, spicy: 0, tealike: 0 },
    'butter': { fruity: -1, nutty: 5, spicy: 0, tealike: 0 },
    'isovaleriansäure': { fruity: 2, nutty: -2, spicy: 0, tealike: 0 },
    'buttersäure': { fruity: 0, nutty: 2, spicy: 0, tealike: 0 },
    'milchshake': { fruity: 1, nutty: 5, spicy: 0, tealike: 0 },
    'sauer/vergoren': { fruity: 3, nutty: -1, spicy: 0, tealike: 0 },

    // Würzig - High spicy scores
    'chili': { fruity: -2, nutty: 0, spicy: 10, tealike: 0 },
    'umami': { fruity: -1, nutty: 2, spicy: 5, tealike: 0 },
    'chai': { fruity: 1, nutty: 3, spicy: 8, tealike: 4 },
    'ingwer': { fruity: 1, nutty: 0, spicy: 9, tealike: 2 },
    'sandelholz': { fruity: -1, nutty: 1, spicy: 4, tealike: 3 },
    'spekulatius': { fruity: 0, nutty: 5, spicy: 6, tealike: 0 },
    'zedernholz': { fruity: -1, nutty: 1, spicy: 3, tealike: 2 },
    'süßholz': { fruity: 1, nutty: 2, spicy: 3, tealike: 3 },
    'scharf': { fruity: -2, nutty: 0, spicy: 10, tealike: 0 },
    'würzig': { fruity: -1, nutty: 1, spicy: 10, tealike: 0 },
    'lakritz': { fruity: 0, nutty: 2, spicy: 4, tealike: 2 },

    // Süss
    'zucker': { fruity: 1, nutty: 3, spicy: 0, tealike: 0 },
    'süss': { fruity: 2, nutty: 4, spicy: 0, tealike: 0 },
    'süsse aromen': { fruity: 2, nutty: 4, spicy: 0, tealike: 0 },

    // Vanille
    'vanille': { fruity: 1, nutty: 6, spicy: 0, tealike: 0 },
    'vanillin': { fruity: 1, nutty: 6, spicy: 0, tealike: 0 },

    // Gemüse
    'süßkartoffel': { fruity: 0, nutty: 3, spicy: 0, tealike: 0 },
    'piment': { fruity: 0, nutty: 0, spicy: 8, tealike: 0 },
    'gemüse': { fruity: 0, nutty: -1, spicy: 0, tealike: 2 },
    'avocado': { fruity: 0, nutty: 2, spicy: 0, tealike: 0 },
    'rhabarber': { fruity: 5, nutty: -1, spicy: 0, tealike: 2 },
    'paprika': { fruity: 1, nutty: -1, spicy: 5, tealike: 0 },
    'kohlrabi': { fruity: 0, nutty: -2, spicy: 0, tealike: 1 },
    'rote beete': { fruity: 2, nutty: 0, spicy: 0, tealike: 1 },
    'grüner tee': { fruity: 3, nutty: 0, spicy: 0, tealike: 10 },

    // Alkohol/Vergoren
    'eierlikör': { fruity: 0, nutty: 5, spicy: 0, tealike: 0 },
    'cognac': { fruity: 2, nutty: 3, spicy: 2, tealike: 0 },
    'likör': { fruity: 2, nutty: 4, spicy: 0, tealike: 0 },
    'überreif': { fruity: 6, nutty: 0, spicy: 0, tealike: 0 },
    'alkohol/vergoren': { fruity: 3, nutty: 1, spicy: 1, tealike: 0 },
    'bourbon': { fruity: -1, nutty: 4, spicy: 3, tealike: 0 },
    'rotwein': { fruity: 5, nutty: 0, spicy: 1, tealike: 0 },
    'portwein': { fruity: 4, nutty: 2, spicy: 1, tealike: 0 },
    'vergoren': { fruity: 3, nutty: 0, spicy: 0, tealike: 0 },
    'whisky': { fruity: -1, nutty: 3, spicy: 4, tealike: 0 },
    'rum': { fruity: 1, nutty: 4, spicy: 2, tealike: 0 },
    'amaretto': { fruity: 0, nutty: 7, spicy: 0, tealike: 0 },
    'baileys': { fruity: 0, nutty: 6, spicy: 0, tealike: 0 },
    'weinig': { fruity: 5, nutty: 0, spicy: 0, tealike: 0 },

    // Bohnenartig
    'bohnenartig': { fruity: 0, nutty: -1, spicy: 0, tealike: 0 },
    'tonkabohne': { fruity: 0, nutty: 5, spicy: 2, tealike: 0 },

    // Beeren
    'wildbeeren': { fruity: 10, nutty: 0, spicy: 0, tealike: 0 },
    'physalis': { fruity: 9, nutty: 0, spicy: 0, tealike: 0 },
    'waldfrucht': { fruity: 10, nutty: 0, spicy: 0, tealike: 0 },
    'dunkle beeren': { fruity: 9, nutty: 0, spicy: 0, tealike: 0 },
    'erdbeere': { fruity: 10, nutty: 0, spicy: 0, tealike: 0 },
    'brombeere': { fruity: 10, nutty: 0, spicy: 0, tealike: 0 },
    'beeren': { fruity: 10, nutty: 0, spicy: 0, tealike: 0 },
    'rote beeren': { fruity: 10, nutty: 0, spicy: 0, tealike: 0 },
    'rote johannisbeere': { fruity: 10, nutty: 0, spicy: 0, tealike: 0 },
    'stachelbeere': { fruity: 8, nutty: 0, spicy: 0, tealike: 0 },
    'wacholderbeere': { fruity: 7, nutty: 0, spicy: 3, tealike: 0 },
    'cranberry': { fruity: 9, nutty: 0, spicy: 0, tealike: 0 },
    'schwarze johannisbeere': { fruity: 10, nutty: 0, spicy: 0, tealike: 0 },
    'himbeere': { fruity: 10, nutty: 0, spicy: 0, tealike: 0 },
    'maulbeere': { fruity: 9, nutty: 0, spicy: 0, tealike: 0 },
    'heidelbeere': { fruity: 10, nutty: 0, spicy: 0, tealike: 0 },

    // Schwarzer Tee - High tealike
    'darjeeling tee': { fruity: 3, nutty: 0, spicy: 0, tealike: 10 },
    'schwarzer tee': { fruity: 2, nutty: 0, spicy: 0, tealike: 10 },
    'ceylontee': { fruity: 3, nutty: 0, spicy: 0, tealike: 10 },

    // Braune Gewürze - High spicy
    'anis': { fruity: 0, nutty: 2, spicy: 7, tealike: 2 },
    'braune gewürze': { fruity: -1, nutty: 3, spicy: 9, tealike: 0 },
    'kardamon': { fruity: 0, nutty: 2, spicy: 9, tealike: 2 },
    'zimt': { fruity: 1, nutty: 4, spicy: 9, tealike: 0 },
    'kurkuma': { fruity: 0, nutty: 1, spicy: 7, tealike: 0 },
    'nelke': { fruity: 0, nutty: 2, spicy: 9, tealike: 0 },
    'lebkuchen': { fruity: 0, nutty: 5, spicy: 7, tealike: 0 },
    'muskatnuss': { fruity: 0, nutty: 3, spicy: 8, tealike: 0 },

    // Brauner Zucker
    'karamellisiert': { fruity: 0, nutty: 7, spicy: 0, tealike: 0 },
    'ahornsirup': { fruity: 1, nutty: 6, spicy: 0, tealike: 0 },
    'honig': { fruity: 2, nutty: 6, spicy: 0, tealike: 2 },
    'blatthonig': { fruity: 2, nutty: 5, spicy: 0, tealike: 2 },
    'keks': { fruity: 0, nutty: 6, spicy: 0, tealike: 0 },
    'biskuit': { fruity: 0, nutty: 5, spicy: 0, tealike: 0 },
    'honigbrot': { fruity: 1, nutty: 6, spicy: 0, tealike: 0 },
    'torte': { fruity: 2, nutty: 6, spicy: 0, tealike: 0 },
    'zuckerrohr': { fruity: 2, nutty: 5, spicy: 0, tealike: 0 },
    'brauner zucker': { fruity: 1, nutty: 5, spicy: 0, tealike: 0 },
    'agavensirup': { fruity: 2, nutty: 4, spicy: 0, tealike: 0 },
    'gesalzener karamell': { fruity: 0, nutty: 7, spicy: 0, tealike: 0 },
    'toffee': { fruity: 0, nutty: 7, spicy: 0, tealike: 0 },
    'sirup': { fruity: 1, nutty: 5, spicy: 0, tealike: 0 },
    'buttertoffee': { fruity: 0, nutty: 7, spicy: 0, tealike: 0 },
    'waffel': { fruity: 0, nutty: 6, spicy: 0, tealike: 0 },
    'süßes gebäck': { fruity: 1, nutty: 6, spicy: 0, tealike: 0 },
    'apfelkuchen': { fruity: 5, nutty: 4, spicy: 2, tealike: 0 },
    'melasse': { fruity: 0, nutty: 5, spicy: 0, tealike: 0 },
    'kandis': { fruity: 1, nutty: 5, spicy: 0, tealike: 0 },

    // Verbrannt
    'röstaromen': { fruity: -5, nutty: 2, spicy: 2, tealike: -2 },
    'aschig': { fruity: -5, nutty: 0, spicy: 0, tealike: -3 },
    'verbrannt': { fruity: -6, nutty: 0, spicy: 0, tealike: -3 },
    'beissend': { fruity: -4, nutty: 0, spicy: 5, tealike: -2 },
    'braun, braten': { fruity: -4, nutty: 3, spicy: 2, tealike: -2 },
    'rauchig': { fruity: -4, nutty: 1, spicy: 4, tealike: -2 },

    // Halmfrüchte
    'malzig': { fruity: -2, nutty: 5, spicy: 0, tealike: 0 },
    'halmfrüchte': { fruity: -2, nutty: 3, spicy: 0, tealike: 0 },
    'getreide': { fruity: -2, nutty: 4, spicy: 0, tealike: 0 },
    'brot': { fruity: -2, nutty: 5, spicy: 0, tealike: 0 },
    'popkorn': { fruity: 0, nutty: 4, spicy: 0, tealike: 0 },
    'bretzel': { fruity: -1, nutty: 5, spicy: 0, tealike: 0 },
    'cornflakes': { fruity: 0, nutty: 4, spicy: 0, tealike: 0 },

    // Chemisch
    'gummi': { fruity: -2, nutty: -2, spicy: 0, tealike: -2 },
    'mineralisch': { fruity: 0, nutty: 0, spicy: 0, tealike: 0 },
    'salzig': { fruity: -1, nutty: 1, spicy: 2, tealike: 0 },
    'stinkig': { fruity: -4, nutty: -3, spicy: 0, tealike: -3 },
    'chemisch': { fruity: -3, nutty: -3, spicy: 0, tealike: -3 },
    'medizinartig': { fruity: -3, nutty: -2, spicy: 0, tealike: -2 },
    'bitter': { fruity: -3, nutty: 0, spicy: 2, tealike: 2 },
    'ölig': { fruity: -2, nutty: 2, spicy: 0, tealike: 0 },
    'meersalz': { fruity: -1, nutty: 1, spicy: 2, tealike: 0 },

    // Zitrusfrüchte
    'helle zitrusfrüchte': { fruity: 10, nutty: 0, spicy: 0, tealike: 0 },
    'zitrusfrüchte': { fruity: 9, nutty: 0, spicy: 0, tealike: 0 },
    'blutorange': { fruity: 10, nutty: 0, spicy: 0, tealike: 0 },
    'pinke grapefruit': { fruity: 9, nutty: 0, spicy: 0, tealike: 0 },
    'clementine': { fruity: 10, nutty: 0, spicy: 0, tealike: 0 },
    'zitrone': { fruity: 9, nutty: 0, spicy: 0, tealike: 0 },
    'mandarine': { fruity: 10, nutty: 0, spicy: 0, tealike: 0 },
    'tangerine': { fruity: 10, nutty: 0, spicy: 0, tealike: 0 },
    'pomelo': { fruity: 9, nutty: 0, spicy: 0, tealike: 0 },
    'grapefruit': { fruity: 8, nutty: -1, spicy: 0, tealike: 0 },
    'limette': { fruity: 9, nutty: 0, spicy: 0, tealike: 0 },
    'bergamotte': { fruity: 8, nutty: 0, spicy: 0, tealike: 4 },
    'orange': { fruity: 10, nutty: 0, spicy: 0, tealike: 0 }
  };

  /**
   * Calculate taste score for a product based on its taste notes
   * Returns scores for fruity, nutty, spicy, and tealike preferences
   * 
   * IMPORTANT: Uses smart matching to prevent substring double-counting.
   * For example, "Zartbitterschokolade" should only count once (as zartbitterschokolade),
   * not additionally as "schokolade" and "bitterschokolade".
   */
  function calculateTasteScore(tasteNotes) {
    const text = tasteNotes.toLowerCase();
    const scores = { fruity: 0, nutty: 0, spicy: 0, tealike: 0 };
    const matchedTastes = [];

    // Split taste notes into individual notes (comma-separated)
    const individualNotes = text.split(',').map(note => note.trim()).filter(note => note.length > 0);

    // Track which positions in the text have been matched to prevent overlapping matches
    const matchedPositions = new Set();

    // Sort taste keys by length (longest first) to prioritize specific matches
    const sortedTastes = Object.entries(TASTE_SCORES).sort((a, b) => b[0].length - a[0].length);

    // Process each individual taste note separately
    for (const note of individualNotes) {
      // For each note, find the single best (longest/most specific) match
      let bestMatch = null;
      let bestMatchLength = 0;

      for (const [taste, tasteScores] of sortedTastes) {
        if (note.includes(taste) && taste.length > bestMatchLength) {
          bestMatch = { taste, ...tasteScores };
          bestMatchLength = taste.length;
        }
      }

      // If we found a match for this note, use only that one (the most specific)
      if (bestMatch) {
        // Check if we already matched this exact taste (avoid duplicates)
        const alreadyMatched = matchedTastes.some(m => m.taste === bestMatch.taste);
        if (!alreadyMatched) {
          scores.fruity += bestMatch.fruity;
          scores.nutty += bestMatch.nutty;
          scores.spicy += bestMatch.spicy;
          scores.tealike += bestMatch.tealike;
          matchedTastes.push(bestMatch);
        }
      }
    }

    return { scores, matchedTastes };
  }

  /**
   * Get the score for a specific taste preference
   */
  function getPreferenceScore(tasteNotes, tastePreference) {
    const { scores, matchedTastes } = calculateTasteScore(tasteNotes);

    let preferenceScore = 0;
    switch (tastePreference) {
      case 'fruity':
        preferenceScore = scores.fruity;
        break;
      case 'nutty':
        preferenceScore = scores.nutty;
        break;
      case 'spicy':
        preferenceScore = scores.spicy;
        break;
      case 'tealike':
        preferenceScore = scores.tealike;
        break;
      default:
        // Fallback: use a balanced approach
        preferenceScore = (scores.fruity + scores.nutty + scores.spicy + scores.tealike) / 4;
    }

    return { preferenceScore, allScores: scores, matchedTastes };
  }

  /**
   * Get human-readable labels
   */
  function getBrewingLabel(brewing) {
    const labels = {
      espresso: 'Siebträger / Mokakanne',
      filter: 'Filterkaffee',
      automatic: 'Vollautomat',
      flexible: 'Vielseitig'
    };
    return labels[brewing] || '';
  }

  function getTasteLabel(taste) {
    const labels = {
      fruity: 'Fruchtig & hell',
      nutty: 'Nussig & schokoladig',
      spicy: 'Würzig',
      tealike: 'Teeartig'
    };
    return labels[taste] || '';
  }

  /**
   * Display the results with pagination
   */
  function displayResults(sectionId) {
    const state = quizStates[sectionId];
    const allResults = state.allResults || [];
    const currentPage = state.currentPage || 0;
    const productsPerPage = state.productsPerPage || 3;
    const matchReason = state.matchReason || '';

    const container = document.getElementById(`coffee-quiz-${sectionId}`);
    hideAllStates(container);

    // Hide reset button on results
    const resetWrapper = document.getElementById(`quiz-reset-wrapper-${sectionId}`);
    if (resetWrapper) {
      resetWrapper.classList.remove('active');
    }

    // Match reason removed per design update
    const reasonElement = document.getElementById(`quiz-match-reason-${sectionId}`);
    if (reasonElement) {
      reasonElement.style.display = 'none';
    }

    // Calculate pagination
    const totalProducts = allResults.length;
    const totalPages = Math.ceil(totalProducts / productsPerPage);
    const startIndex = currentPage * productsPerPage;
    const endIndex = Math.min(startIndex + productsPerPage, totalProducts);
    const currentProducts = allResults.slice(startIndex, endIndex);

    // Render product cards
    const productsContainer = document.getElementById(`quiz-products-${sectionId}`);
    if (productsContainer) {
      if (totalProducts === 0) {
        productsContainer.innerHTML = '<p class="quiz-no-results">Leider haben wir keine passenden Produkte gefunden. Versuche es mit anderen Antworten!</p>';
      } else {
        productsContainer.innerHTML = currentProducts.map(product => {
          const priceFormatted = formatMoney(product.price);
          const matchPercent = product.matchPercent || 75;

          // Helper function to create segment bar
          const createSegmentBar = (value, maxSegments = 10) => {
            const activeCount = value ? parseInt(value) : 0;
            let segments = '';
            for (let i = 1; i <= maxSegments; i++) {
              segments += `<span class="quiz-meta-segment${i <= activeCount ? ' active' : ''}"></span>`;
            }
            return segments;
          };

          // Build metafields HTML - always show all fields
          const hasAromas = product.tasteNotes && String(product.tasteNotes).trim();
          const hasRoast = product.roastLevel && parseInt(product.roastLevel) > 0;
          const hasStrength = product.strength && parseInt(product.strength) > 0;
          const hasAcidity = product.acidity && parseInt(product.acidity) > 0;

          // Escape HTML in tasteNotes to prevent XSS and template breaking
          const escapeHtml = (str) => {
            if (!str) return '';
            return String(str).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;').replace(/'/g, '&#039;').replace(/`/g, '&#096;');
          };
          // Add space after commas in aromas
          const formatAromas = (str) => {
            if (!str) return '';
            return String(str).replace(/,(?!\s)/g, ', ');
          };
          const aromasText = hasAromas ? escapeHtml(formatAromas(product.tasteNotes)) : '—';

          const metafieldsHtml = '<div class="quiz-product-metafields">' +
            '<div class="quiz-product-meta quiz-product-meta-text' + (!hasAromas ? ' disabled' : '') + '">' +
            '<span class="quiz-product-meta-label">Flavor</span>' +
            '<span class="quiz-product-meta-value">' + aromasText + '</span>' +
            '</div>' +
            '<div class="quiz-product-meta quiz-product-meta-bar' + (!hasRoast ? ' disabled' : '') + '">' +
            '<span class="quiz-product-meta-label">Röstgrad</span>' +
            '<div class="quiz-meta-bar">' + createSegmentBar(product.roastLevel) + '</div>' +
            '</div>' +
            '<div class="quiz-product-meta quiz-product-meta-bar' + (!hasStrength ? ' disabled' : '') + '">' +
            '<span class="quiz-product-meta-label">Stärke</span>' +
            '<div class="quiz-meta-bar">' + createSegmentBar(product.strength) + '</div>' +
            '</div>' +
            '<div class="quiz-product-meta quiz-product-meta-bar' + (!hasAcidity ? ' disabled' : '') + '">' +
            '<span class="quiz-product-meta-label">Säure</span>' +
            '<div class="quiz-meta-bar">' + createSegmentBar(product.acidity) + '</div>' +
            '</div>' +
            '</div>';

          return `
            <div class="quiz-product-card">
              <div class="quiz-match-badge">${matchPercent}% Match</div>
              <a href="${product.url}" class="quiz-product-link">
                <div class="quiz-product-image">
                  <img src="${product.image}" alt="${product.title}" loading="lazy">
                </div>
                <div class="quiz-product-info">
                  <h4 class="quiz-product-title">${product.title}</h4>
                  <p class="quiz-product-vendor">${product.vendor}</p>
                  ${metafieldsHtml}
                  <p class="quiz-product-price">${priceFormatted}</p>
                </div>
              </a>
              <a href="${product.url}" class="quiz-product-button">Jetzt kaufen</a>
            </div>
          `;
        }).join('');
      }
    }

    // Render pagination controls
    renderPagination(sectionId, currentPage, totalPages, totalProducts);

    // Enable repeat button in sidebar
    const repeatButton = document.getElementById(`quiz-sidebar-repeat-${sectionId}`);
    if (repeatButton) {
      const button = repeatButton.querySelector('.quiz-repeat-button');
      if (button) {
        button.classList.remove('disabled');
      }
    }

    // Show results state
    const resultsState = container.querySelector('.quiz-results');
    if (resultsState) {
      resultsState.classList.add('active');
    }
  }

  /**
   * Render pagination controls
   */
  function renderPagination(sectionId, currentPage, totalPages, totalProducts) {
    const paginationContainer = document.getElementById(`quiz-pagination-${sectionId}`);
    if (!paginationContainer) return;

    if (totalPages <= 1) {
      paginationContainer.innerHTML = '';
      paginationContainer.classList.remove('has-pagination');
      return;
    }

    paginationContainer.classList.add('has-pagination');
    paginationContainer.innerHTML = `
      <button class="quiz-pagination-arrow quiz-pagination-prev" 
              onclick="coffeeQuiz.prevPage('${sectionId}')" 
              ${currentPage === 0 ? 'disabled' : ''}
              aria-label="Vorherige Seite">
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <polyline points="15 18 9 12 15 6"></polyline>
        </svg>
      </button>
      <button class="quiz-pagination-arrow quiz-pagination-next" 
              onclick="coffeeQuiz.nextPage('${sectionId}')" 
              ${currentPage >= totalPages - 1 ? 'disabled' : ''}
              aria-label="Nächste Seite">
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <polyline points="9 18 15 12 9 6"></polyline>
        </svg>
      </button>
    `;
  }

  /**
   * Go to next page of results
   */
  function nextPage(sectionId) {
    const state = quizStates[sectionId];
    const totalPages = Math.ceil(state.allResults.length / state.productsPerPage);

    if (state.currentPage < totalPages - 1) {
      state.currentPage++;
      displayResults(sectionId);
    }
  }

  /**
   * Go to previous page of results
   */
  function prevPage(sectionId) {
    const state = quizStates[sectionId];

    if (state.currentPage > 0) {
      state.currentPage--;
      displayResults(sectionId);
    }
  }

  /**
   * Reset the quiz
   */
  function resetQuiz(sectionId) {
    const state = quizStates[sectionId];
    const container = document.getElementById(`coffee-quiz-${sectionId}`);

    // Reset state
    state.currentStep = 0;
    state.answers = {
      caffeine: null,
      brewing: null,
      taste: null
    };

    // Hide sidebar and reset button
    const sidebar = document.getElementById(`quiz-sidebar-${sectionId}`);
    if (sidebar) {
      sidebar.classList.remove('active');
    }

    const resetWrapper = document.getElementById(`quiz-reset-wrapper-${sectionId}`);
    if (resetWrapper) {
      resetWrapper.classList.remove('active');
    }

    // Disable repeat button
    const repeatButton = document.getElementById(`quiz-sidebar-repeat-${sectionId}`);
    if (repeatButton) {
      const button = repeatButton.querySelector('.quiz-repeat-button');
      if (button) {
        button.classList.add('disabled');
      }
    }

    // Reset UI
    hideAllStates(container);
    const introState = container.querySelector('.quiz-intro');
    if (introState) {
      introState.classList.add('active');
    }

    // Remove answered classes from all buttons
    const buttons = container.querySelectorAll('.quiz-answer');
    buttons.forEach(btn => btn.classList.remove('answered'));
  }

  /**
   * Update progress dots
   */
  function updateProgressBar(sectionId) {
    const state = quizStates[sectionId];
    const sidebar = document.getElementById(`quiz-sidebar-${sectionId}`);
    if (!sidebar) return;

    const dots = sidebar.querySelectorAll('.quiz-progress-dot');
    const progressContainer = sidebar.querySelector('.quiz-sidebar-progress');

    let answeredCount = 0;

    dots.forEach((dot, index) => {
      const step = index + 1;
      const questionKey = questionOrder[index];

      dot.classList.remove('active', 'answered');

      // Check if this question has been answered
      if (state.answers[questionKey] !== null) {
        dot.classList.add('answered');
        answeredCount++;
      }

      // Mark current active question
      if (step === state.currentStep) {
        dot.classList.add('active');
      }
    });

    // Update progress line gradient
    if (progressContainer && answeredCount > 0) {
      const progressHeight = (answeredCount / TOTAL_QUESTIONS) * 100;
      progressContainer.style.setProperty('--progress-height', progressHeight + '%');
      progressContainer.setAttribute('data-answered', answeredCount);
    } else if (progressContainer) {
      progressContainer.style.setProperty('--progress-height', '0%');
      progressContainer.removeAttribute('data-answered');
    }
  }

  /**
   * Update sidebar with current answers
   */
  function updateSidebar(sectionId) {
    const state = quizStates[sectionId];
    const sidebar = document.getElementById(`quiz-sidebar-${sectionId}`);
    if (!sidebar) return;

    // Question map: caffeine (1), brewing (2), taste (3)
    const questionMap = {
      caffeine: {
        question: 1, labels: {
          full: 'Volle Power',
          decaf: 'Entkoffeiniert'
        }
      },
      brewing: {
        question: 2, labels: {
          espresso: 'Siebträger',
          filter: 'Filterkaffee',
          automatic: 'Vollautomat',
          flexible: 'Flexibel'
        }
      },
      taste: {
        question: 3, labels: {
          fruity: 'Fruchtig & hell',
          nutty: 'Nussig & schokoladig',
          spicy: 'Würzig',
          tealike: 'Teeartig'
        }
      }
    };

    // Update each sidebar item
    Object.keys(questionMap).forEach(key => {
      const answer = state.answers[key];
      const answerElement = document.getElementById(`sidebar-answer-${key}-${sectionId}`);
      const sidebarItem = sidebar.querySelector(`.quiz-sidebar-item[data-question="${questionMap[key].question}"]`);

      if (answerElement && sidebarItem) {
        if (answer) {
          answerElement.textContent = questionMap[key].labels[answer] || answer;
          sidebarItem.classList.add('answered');
        } else {
          answerElement.textContent = '—';
          sidebarItem.classList.remove('answered');
        }

        // Mark current question
        if (state.currentStep === questionMap[key].question) {
          sidebarItem.classList.add('current');
        } else {
          sidebarItem.classList.remove('current');
        }
      }
    });

    // Enable/disable repeat button based on whether any question is answered
    const hasAnyAnswer = Object.values(state.answers).some(answer => answer !== null);
    const repeatButton = document.getElementById(`quiz-sidebar-repeat-${sectionId}`);
    if (repeatButton) {
      const button = repeatButton.querySelector('.quiz-repeat-button');
      if (button) {
        if (hasAnyAnswer) {
          button.classList.remove('disabled');
        } else {
          button.classList.add('disabled');
        }
      }
    }
  }

  /**
   * Setup sidebar click listeners
   */
  function setupSidebarListeners(sectionId) {
    const sidebar = document.getElementById(`quiz-sidebar-${sectionId}`);
    if (!sidebar) return;

    const items = sidebar.querySelectorAll('.quiz-sidebar-item');
    items.forEach(item => {
      item.addEventListener('click', function () {
        if (this.classList.contains('answered')) {
          const questionNumber = parseInt(this.getAttribute('data-question'));
          goToQuestion(sectionId, questionNumber);
        }
      });
    });
  }

  /**
   * Go to a specific question
   */
  function goToQuestion(sectionId, questionNumber) {
    const state = quizStates[sectionId];
    const container = document.getElementById(`coffee-quiz-${sectionId}`);

    // Clear answers for this question and all subsequent questions
    for (let i = questionNumber - 1; i < questionOrder.length; i++) {
      state.answers[questionOrder[i]] = null;
    }

    // Update current step
    state.currentStep = questionNumber;

    // Show the question
    hideAllStates(container);
    showQuestion(container, questionNumber);

    // Update sidebar and progress
    updateSidebar(sectionId);
    updateProgressBar(sectionId);
  }

  /**
   * Helper: Hide all quiz states
   */
  function hideAllStates(container) {
    const states = container.querySelectorAll('.quiz-state');
    states.forEach(state => state.classList.remove('active'));
  }

  /**
   * Helper: Show specific question
   */
  function showQuestion(container, questionNumber) {
    const question = container.querySelector(`.quiz-question[data-question="${questionNumber}"]`);
    if (question) {
      question.classList.add('active');
    }
  }

  /**
   * Helper: Format money (Shopify format)
   */
  function formatMoney(cents) {
    if (typeof cents === 'string') {
      cents = parseInt(cents, 10);
    }
    const euros = cents / 100;
    return `€${euros.toFixed(2).replace('.', ',')}`;
  }

  // Handle window resize - update productsPerPage for responsive layout
  let resizeTimeout;
  window.addEventListener('resize', function () {
    clearTimeout(resizeTimeout);
    resizeTimeout = setTimeout(function () {
      const newProductsPerPage = window.innerWidth < 990 ? 1 : 3;
      Object.keys(quizStates).forEach(function (sectionId) {
        const state = quizStates[sectionId];
        if (state.allResults && state.productsPerPage !== newProductsPerPage) {
          state.productsPerPage = newProductsPerPage;
          state.currentPage = 0; // Reset to first page
          displayResults(sectionId);
        }
      });
    }, 150);
  });

  // Public API
  return {
    startQuiz,
    selectAnswer,
    resetQuiz,
    nextPage,
    prevPage
  };
})();

// Make available globally
window.coffeeQuiz = coffeeQuiz;
